import type { TimerDisplayFormat, TimerDuration, TimerDurationField, TimerMode } from './types';

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

const FIELD_MAX: Record<TimerDurationField, number> = {
  hours: 99,
  minutes: 59,
  seconds: 59,
};

const FIELD_STEP: Record<TimerDurationField, number> = {
  hours: 1,
  minutes: 1,
  seconds: 30,
};

export function durationToMs(duration: TimerDuration): number {
  return (
    duration.hours * MS_PER_HOUR +
    duration.minutes * MS_PER_MINUTE +
    duration.seconds * MS_PER_SECOND
  );
}

export function msToDuration(ms: number): TimerDuration {
  const totalSeconds = Math.max(0, Math.floor(ms / MS_PER_SECOND));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { hours, minutes, seconds };
}

export function isDurationZero(duration: TimerDuration): boolean {
  return durationToMs(duration) <= 0;
}

export function getDisplayFormat(target: TimerDuration): TimerDisplayFormat {
  if (target.hours > 0) return 'hms';
  if (target.minutes > 0) return 'ms';
  return 's';
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** 按目标时长决定展示精度：有时→HH:MM:SS，无时分→MM:SS，仅秒→SS */
export function formatTimerTime(ms: number, format: TimerDisplayFormat): string {
  const { hours, minutes, seconds } = msToDuration(ms);
  switch (format) {
    case 'hms':
      return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
    case 'ms':
      return `${pad2(minutes)}:${pad2(seconds)}`;
    default:
      return pad2(seconds);
  }
}

export function formatTargetTime(duration: TimerDuration): string {
  return `${pad2(duration.hours)}:${pad2(duration.minutes)}:${pad2(duration.seconds)}`;
}

/** 主显示毫秒：倒计时未开始时显示目标时长，开始后显示剩余；正计时显示已用时间 */
export function getTimerMainDisplayMs(
  mode: TimerMode,
  targetMs: number,
  elapsedMs: number,
  hasStarted: boolean,
): number {
  if (mode === 'countUp') return elapsedMs;
  if (!hasStarted) return targetMs;
  return Math.max(0, targetMs - elapsedMs);
}

export function computeTimerProgress(targetMs: number, elapsedMs: number): number {
  if (targetMs <= 0) return 0;
  return Math.min(100, (elapsedMs / targetMs) * 100);
}

export function toggleTimerMode(mode: TimerMode): TimerMode {
  return mode === 'countDown' ? 'countUp' : 'countDown';
}

export function clampDurationField(field: TimerDurationField, value: number): number {
  return Math.max(0, Math.min(FIELD_MAX[field], Math.floor(value)));
}

export function adjustDurationField(
  duration: TimerDuration,
  field: TimerDurationField,
  direction: 1 | -1,
): TimerDuration {
  const step = FIELD_STEP[field];
  const delta = direction * step;
  let next = { ...duration, [field]: duration[field] + delta };

  if (field === 'seconds') {
    while (next.seconds > FIELD_MAX.seconds) {
      next.seconds -= 60;
      next.minutes += 1;
    }
    while (next.seconds < 0) {
      next.seconds += 60;
      next.minutes -= 1;
    }
  }

  if (field === 'minutes' || next.minutes !== duration.minutes) {
    while (next.minutes > FIELD_MAX.minutes) {
      next.minutes -= 60;
      next.hours += 1;
    }
    while (next.minutes < 0) {
      next.minutes += 60;
      next.hours -= 1;
    }
  }

  next.hours = clampDurationField('hours', next.hours);
  next.minutes = clampDurationField('minutes', next.minutes);
  next.seconds = clampDurationField('seconds', next.seconds);
  return next;
}

export function parseDurationFieldInput(
  field: TimerDurationField,
  raw: string,
): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return clampDurationField(field, Number(trimmed));
}
